import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const LIKELIHOOD_KEYS = ["almostCertain", "likely", "possible", "unlikely", "rare"];
const IMPACT_KEYS = ["insignificant", "minor", "moderate", "major", "catastrophic"];

const RiskMatrix = ({ height, points }) => {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    const [hoveredPoint, setHoveredPoint] = useState(null);
    const [lineCoordinates, setLineCoordinates] = useState([]);

    useEffect(() => {
        const updateSize = () => {
            if (containerRef.current) {
                const width = containerRef.current.offsetWidth;
                const height = containerRef.current.offsetHeight;
                setContainerSize({ width, height });
            }
        };

        updateSize();
        window.addEventListener("resize", updateSize);
        return () => window.removeEventListener("resize", updateSize);
    }, []);

    const getColor = (x, y) => {
        const colors = [
            ["bg-[#09A6B0]", "bg-[#FED4AE]", "bg-[#F07167]", "bg-[#F07167]", "bg-[#F07167]"],
            ["bg-[#09A6B0]", "bg-[#FED4AE]", "bg-[#FED4AE]", "bg-[#F07167]", "bg-[#F07167]"],
            ["bg-[#09A6B0]", "bg-[#09A6B0]", "bg-[#FED4AE]", "bg-[#FED4AE]", "bg-[#F07167]"],
            ["bg-[#05769D]", "bg-[#09A6B0]", "bg-[#09A6B0]", "bg-[#FED4AE]", "bg-[#FED4AE]"],
            ["bg-[#05769D]", "bg-[#05769D]", "bg-[#09A6B0]", "bg-[#09A6B0]", "bg-[#FED4AE]"]
        ];
        return colors[y][x];
    };

    const calculatePointPosition = (colIndex, rowIndex) => {
        const cell = document.querySelector(`#cell-${rowIndex}-${colIndex}`);
        if (cell && containerRef.current) {
            const rect = cell.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();
            return {
                x: rect.left + rect.width / 2 - containerRect.left,
                y: rect.top + rect.height / 2 - containerRect.top
            };
        }
        return { x: 0, y: 0 };
    };

    const riskTypes = ["Inherent", "Residual", "Future"];

    useEffect(() => {
        // If points is null/undefined/empty -> clear the dashed line
        if (!Array.isArray(points) || points.length === 0) {
            setLineCoordinates([]);
            setHoveredPoint(null);
            return;
        }

        const newLineCoordinates = points.map(({ x, y }) => {
            return calculatePointPosition(x, 4 - y);
        });

        setLineCoordinates(newLineCoordinates);
    }, [points, containerSize]);

    return (
        <div ref={containerRef} className="w-full flex items-center justify-center overflow-hidden relative" style={{ height }}>
            <div className="grid grid-rows-5 grid-cols-6 gap-1 w-full h-full p-2 pb-2">
                {LIKELIHOOD_KEYS.map((likelihoodKey, rowIndex) => (
                    <React.Fragment key={rowIndex}>
                        <div className="flex items-center pr-2 text-xs font-medium ml-2">
                            {t(`generalDashboard.matrix.${likelihoodKey}`)}
                        </div>

                        {[...Array(5)].map((_, colIndex) => {
                            const point = points?.find((p) => p.x === colIndex && p.y === 4 - rowIndex);
                            return (
                                <div
                                    key={`${rowIndex}-${colIndex}`}
                                    id={`cell-${rowIndex}-${colIndex}`}
                                    className={`relative flex items-center justify-center rounded-md ${getColor(colIndex, rowIndex)}`}
                                    style={{
                                        height: `${containerSize.height / 5 - 10}px`,
                                    }}
                                >
                                    {point && (
                                        <div
                                            className="flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-xl text-black z-10 opacity-90 relative"
                                            onMouseEnter={() => {
                                                const index = points.findIndex(p => p.x === colIndex && p.y === 4 - rowIndex);
                                                setHoveredPoint({
                                                    x: colIndex,
                                                    y: 4 - rowIndex,
                                                    value: point.value,
                                                    type: riskTypes[index] // Assign risk type based on index
                                                });
                                            }}
                                            onMouseLeave={() => setHoveredPoint(null)}
                                        >
                                            {point.value}
                                            {hoveredPoint?.x === colIndex && hoveredPoint?.y === 4 - rowIndex && (
                                            <div
                                            className="absolute left-12 top-[-5px] bg-white z-50 text-black text-sm p-2 shadow-lg opacity-100 border border-gray-300"
                                            style={{ minWidth: "80px", whiteSpace: "nowrap" }} // Bigger tooltip box
                                        >
                                            <span className="font-bold text-md">{t(`generalDashboard.series.${String(hoveredPoint.type).toLowerCase()}`)}:</span> <br />
                                            <span className="text-md">{hoveredPoint.value}</span>
                                        </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </React.Fragment>
                ))}

                <div></div>

                {IMPACT_KEYS.map((impactKey, idx) => (
                    <div key={idx} className="text-center text-xs font-medium truncate">
                        {t(`generalDashboard.matrix.${impactKey}`)}
                    </div>
                ))}
            </div>

            {/* Line Connecting Points */}
            <svg
                className="absolute top-0 left-0 w-full h-full shadow-xl pointer-events-none"
                xmlns="http://www.w3.org/2000/svg"
                viewBox={`0 0 ${containerSize.width} ${containerSize.height}`}
            >
                <polyline
                    points={lineCoordinates.map(({ x, y }) => `${x},${y}`).join(' ')}
                    fill="none"
                    stroke="black"
                    strokeWidth="2"
                    opacity="0.8"
                    strokeDasharray="5,5"
                />
            </svg>
        </div>
    );
};

export default RiskMatrix;